import { notifications } from "@mantine/notifications";
import { useCallback, useMemo, useState } from "react";
import { useDisclosure } from "@mantine/hooks";
import {
  SystemEmailMode,
  SystemEmailRegistryEntry,
} from "../../services/admin/systemEmails.service";
import { useUpdateSystemEmailMode } from "./useSystemEmails";

type Mutation = ReturnType<typeof useUpdateSystemEmailMode>;

interface UpdateModeOptions {
  entry: SystemEmailRegistryEntry;
  mode: SystemEmailMode;
  mutation: Mutation;
  onSuccess: () => void;
}

function showModeUpdated(
  entry: SystemEmailRegistryEntry,
  mode: SystemEmailMode
) {
  notifications.show({
    color: "green",
    message: `${entry.label} is now ${mode}.`,
    title: "Mode updated",
  });
}

function showModeUpdateError(error: unknown) {
  notifications.show({
    color: "red",
    message: (error as Error).message,
    title: "Mode not changed",
  });
}

async function updateMode(options: UpdateModeOptions): Promise<void> {
  const { entry, mode, mutation, onSuccess } = options;
  try {
    await mutation.mutateAsync({
      emailTypeId: entry.id,
      expectedRevision: entry.revision,
      mode,
    });
    showModeUpdated(entry, mode);
    onSuccess();
  } catch (error) {
    showModeUpdateError(error);
  }
}

export function useSystemEmailModeDialog() {
  const [mode, setMode] = useState<SystemEmailMode | null>(null);
  const [opened, disclosure] = useDisclosure(false);
  const open = useCallback(
    (nextMode: SystemEmailMode) => {
      setMode(nextMode);
      disclosure.open();
    },
    [disclosure]
  );
  const close = useCallback(() => {
    disclosure.close();
    setMode(null);
  }, [disclosure]);
  return { mode, opened, open, close };
}

export function useSystemEmailModeMutation(
  entry: SystemEmailRegistryEntry,
  mode: SystemEmailMode | null,
  onSuccess: () => void
) {
  const mutation = useUpdateSystemEmailMode();
  const confirm = useCallback(
    () => (mode ? updateMode({ entry, mode, mutation, onSuccess }) : undefined),
    [entry, mode, mutation, onSuccess]
  );
  return { mutation, confirm };
}

export function useSystemEmailModeConfirmModel(
  entry: SystemEmailRegistryEntry,
  dialog: ReturnType<typeof useSystemEmailModeDialog>,
  update: ReturnType<typeof useSystemEmailModeMutation>
) {
  return useMemo(
    () => ({
      emailLabel: entry.label,
      mode: dialog.mode,
      opened: dialog.opened,
      loading: update.mutation.isPending,
      error: update.mutation.error?.message ?? null,
      onClose: dialog.close,
      onConfirm: update.confirm,
    }),
    [dialog, entry.label, update]
  );
}
